//* Library Imports
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

//* Hook Import
import { useGetProcessById } from '../../hooks/queries/process/use-get-process';
import { usePermissionChecker } from "../../hooks/usePermissionChecker";

//* Component Import
import { ErrorMessages } from "../../component/common/ErrorMessages";

//* Skeleton Import
import { Loading } from '../../component/common/Loading';

interface WithPermissionCheckProps {
  children: React.ReactNode;
}

/**
 * Higher-Order Component that wraps content with permission checking
 * Prevents unauthorized access to protected routes
 */
export function WithPermissionCheck({ children }: WithPermissionCheckProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const processId = id ? parseInt(id) : 0;
  const processQuery = useGetProcessById(processId);
  const { isLoading, isError, data: processResponse } = processQuery;
  
  // Permission checking
  const permissionCheck = usePermissionChecker(
    processId, 
    processResponse?.data?.faculdadePermissions
  );

  // Invalid process ID
  if (!processId) {
    navigate("/");
    return null;
  }

  const handleBack = () => {
    navigate("/");
  };

  // Loading state
  if (isLoading) {
    return <div className="d-flex justify-content-center align-items-center vh-100"><Loading /></div>; 
    
  }

  // Error loading process data
  if (isError || !processResponse?.success || !processResponse?.data) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <ErrorMessages
          messages={[
            processResponse?.message ||
              "Não foi possível carregar os detalhes do processo.",
          ]}
          clearErrors={handleBack}
        />
      </div>
    );
  }

  // Permission denied - show 404 for security reasons
  if (!permissionCheck.hasAccess) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <ErrorMessages
          messages={[
            "Página não encontrada.",
            "O conteúdo solicitado não existe ou foi removido."
          ]}
          clearErrors={handleBack}
        />
      </div>
    );
  }

  // Access granted - render the protected content
  return <>{children}</>;
}

/**
 * Hook to create a protected component with permission checking
 * @param Component - The component to be protected
 * @returns Protected component wrapped with permission checking
 */
export function withPermissionCheck<P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> {
  const ProtectedComponent = (props: P) => (
    <WithPermissionCheck>
      <Component {...props} />
    </WithPermissionCheck>
  );

  ProtectedComponent.displayName = `withPermissionCheck(${Component.displayName || Component.name})`;
  
  return ProtectedComponent;
}
